import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MarkdownLite } from "@/components/MarkdownLite";
import { Send, Sparkles, X, ArrowUpRight, Plus, History, Trash2, ChevronDown, ShieldCheck, AlertTriangle, ExternalLink, Target } from "lucide-react";
import { api, mediaSrc } from "@/lib/api";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";
import { SourceBadge } from "@/components/SourceBadge";

const DEFAULT_SUGGESTIONS = ["What am I still missing?", "Find a cheaper rain shell", "Show elk hunts under $8k"];

function suggestionsFor(ctx) {
  if (ctx && ctx.state) {
    const sp = ctx.species || "elk";
    return [
      `Which ${ctx.state} ${sp.toLowerCase()} hunts have the best draw odds?`,
      `Show ${ctx.state} ${sp.toLowerCase()} hunts over 15% odds`,
      `How fast is point creep for ${ctx.state} ${sp.toLowerCase()}?`,
    ];
  }
  return DEFAULT_SUGGESTIONS;
}

export function AIDrawer() {
  const { aiOpen, setAiOpen, aiSeed, aiAutoSend, aiContext } = useUI();
  const { isAuthed } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [convId, setConvId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [convos, setConvos] = useState([]);
  const scrollRef = useRef(null);
  const sentRef = useRef(null);
  const [aiChips, setAiChips] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [personalOn, setPersonalOn] = useState(true);
  const [chipsOpen, setChipsOpen] = useState(false);

  useEffect(() => {
    if (aiOpen && isAuthed) api.aiContext().then((d) => setAiChips(d.chips || [])).catch(() => setAiChips([]));
  }, [aiOpen, isAuthed]);

  const loadConvos = async () => {
    try { setConvos(await api.aiConversations()); } catch { setConvos([]); }
  };
  const openHistory = async () => { setShowHistory(true); await loadConvos(); };
  const openConversation = async (id) => {
    try {
      const conv = await api.aiConversation(id);
      setMessages((conv.messages || []).map((m) => ({
        role: m.role, text: m.text, sources: m.sources || [], links: [],
      })));
      setConvId(id);
      setShowHistory(false);
    } catch { /* ignore */ }
  };
  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    try { await api.deleteAiConversation(id); if (id === convId) newChat(); await loadConvos(); } catch { /* ignore */ }
  };
  const newChat = () => { setMessages([]); setCtx(null); setInput(""); setConvId(null); };

  useEffect(() => {
    if (aiOpen && aiSeed) setInput(aiSeed);
    if (!aiOpen) sentRef.current = null;
  }, [aiOpen, aiSeed]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    const history = messages.filter((m) => m.text).map((m) => ({ role: m.role === "user" ? "user" : "ai", text: m.text })).slice(-6);
    setMessages((m) => [...m, { role: "user", text: q }, { role: "ai", text: "", status: "Thinking…", links: [], sources: [] }]);
    setInput("");
    setBusy(true);
    const setLast = (patch) => setMessages((m) => {
      if (m.length === 0) return m;
      const copy = [...m];
      const i = copy.length - 1;
      if (copy[i].role !== "ai") return m;
      copy[i] = { ...copy[i], ...(typeof patch === "function" ? patch(copy[i]) : patch) };
      return copy;
    });
    try {
      await api.streamAI({ query: q, page_context: aiContext || undefined, history, conversation_id: convId || undefined,
        personalization: isAuthed ? { enabled: personalOn, exclude: excluded } : undefined }, isAuthed, {
        onMeta: (ev) => setLast({ status: ev.status }),
        onDelta: (chunk) => setLast((cur) => ({ status: null, text: (cur.text || "") + chunk })),
        onDone: (ev) => { setLast({ status: null, text: ev.response || "", links: ev.linked_hunts || [], sources: ev.sources || [], safeguard: ev.regulation_safeguard || null, plannerCtx: ev.planner_context || null }); setCtx(ev); if (ev.conversation_id) setConvId(ev.conversation_id); },
        onError: () => setLast({ status: null, text: "I couldn't reach the HomeRange intelligence layer just now. Please try again." }),
      });
    } catch {
      setLast({ status: null, text: "I couldn't reach the HomeRange intelligence layer just now. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  // Auto-send a seeded question (from the HomeRange AI page input/suggestions).
  useEffect(() => {
    if (aiOpen && aiAutoSend && aiSeed && sentRef.current !== aiSeed && !busy) {
      sentRef.current = aiSeed;
      send(aiSeed);
    }
  }, [aiOpen, aiAutoSend, aiSeed]);

  return (
    <AnimatePresence>
      {aiOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[65] bg-black/60 backdrop-blur-sm" onClick={() => setAiOpen(false)} />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 z-[66] h-full w-full max-w-md bg-[#0B0B0C]/98 backdrop-blur-xl border-l border-[#232326] text-[#F5F5F5] flex flex-col font-sans"
            data-testid="ai-drawer"
          >
            {showHistory && (
              <div className="absolute inset-0 z-10 bg-[#0B0B0C] flex flex-col" data-testid="ai-history-panel">
                <div className="flex items-center justify-between p-6 pb-4 border-b border-[#232326]">
                  <h2 className="font-serif text-2xl text-[#F5F5F5]">My AI conversations</h2>
                  <button onClick={() => setShowHistory(false)} className="text-[#B9B9B9] hover:text-[#FF5A1F] transition-colors" aria-label="Back" data-testid="ai-history-close"><X size={24} /></button>
                </div>
                <div className="flex-1 overflow-auto hr-scroll p-4 space-y-2">
                  {convos.length === 0 && <p className="text-sm text-[#8B8B8F] p-4 text-center">No saved conversations yet. Start chatting and your hunts will show up here.</p>}
                  {convos.map((c) => (
                    <button key={c.id} onClick={() => openConversation(c.id)} className="w-full text-left bg-[#111214] border border-[#232326] hover:border-[#FF5A1F] transition-colors px-3.5 py-3 flex items-center justify-between gap-3 group rounded-lg" data-testid={`ai-convo-${c.id}`}>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-[#F5F5F5] truncate">{c.title}</span>
                        <span className="block text-[11px] text-[#8B8B8F]">{[c.state, c.species].filter(Boolean).join(" · ")}{(c.state || c.species) ? " · " : ""}{new Date(c.updated_at).toLocaleDateString()}</span>
                      </span>
                      <span onClick={(e) => deleteConversation(c.id, e)} className="text-[#5f5f63] hover:text-[#FF5A1F] transition-colors shrink-0" data-testid={`ai-convo-delete-${c.id}`}><Trash2 size={15} /></span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-start justify-between p-6 pb-4 border-b border-[#232326]">
              <div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#FF5A1F]">HomeRange AI</span>
                <h2 className="font-serif text-3xl text-[#F5F5F5] mt-1">Your hunting assistant.</h2>
              </div>
              <div className="flex items-center gap-3">
                {isAuthed && (
                  <button onClick={openHistory} className="text-[#B9B9B9] hover:text-[#FF5A1F] transition-colors" aria-label="Conversation history" data-testid="ai-history-btn"><History size={22} /></button>
                )}
                <button onClick={() => setAiOpen(false)} className="text-[#B9B9B9] hover:text-[#FF5A1F] transition-colors" aria-label="Close" data-testid="ai-close"><X size={26} /></button>
              </div>
            </div>
            {messages.length > 0 && (
              <button onClick={newChat} className="mx-6 mt-3 mb-1 self-start inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#FF5A1F] hover:text-[#FF6D33] transition-colors" data-testid="ai-new-chat"><Plus size={13} /> New hunt chat</button>
            )}

            <div className="mx-6 mt-3 bg-[#111214] border border-[#232326] rounded-xl p-4">
              <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#FF5A1F]">Active context</span>
              <strong className="block mt-1 text-sm text-[#F5F5F5]">{isAuthed ? "Signed in — personalized to your hunt & gear" : "Public mode — sign in to personalize"}</strong>
              {aiContext && (aiContext.state || aiContext.species) && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ai-page-context">
                  {[aiContext.state, aiContext.species, aiContext.unit, aiContext.weapon, aiContext.hunt_style].filter(Boolean).map((c) => (
                    <span key={c} className="text-[11px] bg-[#FF5A1F] text-white px-2 py-0.5 rounded-full">{c}</span>
                  ))}
                </div>
              )}
              {ctx && !ctx.public_mode && ctx.hunt && (
                <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
                  <div className="bg-[#0B0B0C] border border-[#232326] p-2 rounded"><span className="text-[#8B8B8F] block">GEAR LOCKER</span>{ctx.gear_locker_summary.length} checked</div>
                  <div className="bg-[#0B0B0C] border border-[#232326] p-2 rounded"><span className="text-[#8B8B8F] block">TOP MATCH</span>{ctx.recommended_products[0]?.name?.slice(0, 22) || "—"}</div>
                  <div className="bg-[#0B0B0C] border border-[#232326] p-2 rounded"><span className="text-[#8B8B8F] block">HUNT OPTIONS</span>{ctx.hunt_options.length} matches</div>
                  <div className="bg-[#0B0B0C] border border-[#232326] p-2 rounded"><span className="text-[#8B8B8F] block">OFFICIAL LINKS</span>{ctx.official_resources.length}</div>
                </div>
              )}
            </div>

            <div ref={scrollRef} className="flex-1 overflow-auto hr-scroll px-6 py-5 space-y-3">
              {messages.length === 0 && (
                <div className="bg-[#111214] border border-[#232326] text-[#B9B9B9] p-4 text-sm leading-relaxed rounded-xl">
                  Tell me what you&apos;re working on. I can help with gear, hunts, outfitters, regulations, recipes, your journal, or your current hunt.
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div className={`p-3.5 text-sm leading-relaxed max-w-[92%] rounded-xl ${m.role === "user" ? "bg-[#232326] text-[#F5F5F5] rounded-tr-none whitespace-pre-wrap" : "bg-[#111214] border border-[#232326] text-[#E7E7E7] rounded-tl-none"}`} data-testid={`ai-message-${m.role}`}>
                    {m.text ? (
                      m.role === "ai" ? <MarkdownLite text={m.text} /> : m.text
                    ) : (m.status ? (
                      <span className="inline-flex items-center gap-2 text-[#8B8B8F]" data-testid="ai-status"><Sparkles size={14} className="animate-pulse text-[#FF5A1F]" /> {m.status}</span>
                    ) : (m.role === "ai" ? <span className="inline-flex items-center gap-2 text-[#8B8B8F]"><Sparkles size={14} className="animate-pulse text-[#FF5A1F]" /> …</span> : null))}
                  </div>
                  {m.role === "ai" && m.safeguard?.is_regulatory && m.safeguard?.level && (
                    <div
                      className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-[12px] leading-snug ${m.safeguard.level === "verified" ? "border-[#2E7D46]/40 bg-[#2E7D46]/10 text-[#B7E4C7]" : "border-amber-500/40 bg-amber-500/10 text-amber-200"}`}
                      data-testid={`ai-safeguard-${m.safeguard.level}`}
                    >
                      {m.safeguard.confidence && (
                        <div className="flex items-center gap-1.5 mb-1.5" data-testid={`ai-confidence-${m.safeguard.confidence}`} title={m.safeguard.confidence_blurb || ""}>
                          <span className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-70">Regulation confidence</span>
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${m.safeguard.confidence === "verified" ? "border-[#2E7D46]/50 bg-[#2E7D46]/20 text-[#B7E4C7]" : m.safeguard.confidence === "high" ? "border-sky-400/50 bg-sky-400/15 text-sky-200" : "border-amber-400/50 bg-amber-400/15 text-amber-200"}`}>
                            {m.safeguard.confidence === "review" ? <AlertTriangle size={10} /> : <ShieldCheck size={10} />}
                            {m.safeguard.confidence_label}
                          </span>
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        {m.safeguard.level === "verified" ? <ShieldCheck size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
                        <span className="min-w-0">
                          {m.safeguard.message}
                          {m.safeguard.official_link && (
                            <a href={mediaSrc(m.safeguard.official_link)} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-0.5 font-semibold underline hover:no-underline" data-testid="ai-safeguard-official-link">
                              Official source <ExternalLink size={10} />
                            </a>
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                  {m.role === "ai" && m.sources?.length > 0 && (
                    <details className="mt-2 w-full bg-[#111214] border border-[#232326] rounded-lg" data-testid="ai-sources">
                      <summary className="cursor-pointer px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#B9B9B9]">Sources · {m.sources.length}</summary>
                      <div className="px-3 pb-2.5 space-y-2">
                        {m.sources.map((s, si) => (
                          <a key={si} href={s.url ? mediaSrc(s.url) : undefined} target="_blank" rel="noreferrer"
                             className={`flex items-start gap-2 text-[11px] ${s.url ? "text-[#E7E7E7] hover:text-[#FF5A1F]" : "text-[#8B8B8F]"}`} data-testid={`ai-source-${si}`}>
                            <SourceBadge tier={s.source_tier || (s.partner ? "partner" : s.official ? "official" : "third_party")} size="xs" className="shrink-0 mt-0.5" />
                            <span className="min-w-0">{s.title}{s.page ? ` — Page ${s.page}` : ""}{s.agency && !s.web && !s.partner ? ` (${s.agency})` : ""}</span>
                          </a>
                        ))}
                      </div>
                    </details>
                  )}
                  {m.role === "ai" && m.links?.length > 0 && (
                    <div className="mt-2 w-full space-y-1.5" data-testid="ai-links">
                      {m.links.map((l) => (
                        <button
                          key={l.species}
                          onClick={() => { setAiOpen(false); navigate(`/states/${encodeURIComponent(l.state)}/species/${encodeURIComponent(l.species)}`); }}
                          className="w-full text-left bg-[#111214] border border-[#232326] hover:border-[#FF5A1F] transition-colors px-3 py-2.5 flex items-center justify-between gap-2 group rounded-lg"
                          data-testid={`ai-link-${l.species}`}
                        >
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-[#F5F5F5]">{l.species} in {l.state}</span>
                            <span className="block text-[11px] text-[#8B8B8F] truncate">
                              {l.units?.length ? l.units.map((u) => `${u.unit}${u.draw_odds_pct != null ? ` · ${u.draw_odds_pct}%` : ""}`).join("  ·  ") : "View units, seasons & odds"}
                            </span>
                          </span>
                          <ArrowUpRight size={16} className="text-[#FF5A1F] shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </button>
                      ))}
                    </div>
                  )}
                  {m.role === "ai" && m.plannerCtx?.state && (m.plannerCtx?.species || m.plannerCtx?.special) && (
                    <button
                      onClick={() => {
                        setAiOpen(false);
                        const qp = new URLSearchParams({ state: m.plannerCtx.state });
                        if (m.plannerCtx.species) qp.set("species", m.plannerCtx.species);
                        if (m.plannerCtx.points != null) qp.set("points", String(m.plannerCtx.points));
                        if (m.plannerCtx.special) qp.set("special", "1");
                        navigate(`/planner?${qp.toString()}`);
                      }}
                      className="mt-2 w-full bg-[#FF5A1F] hover:bg-[#e04d15] transition-colors text-white px-3 py-2.5 flex items-center justify-between gap-2 group rounded-lg font-semibold text-sm"
                      data-testid="ai-view-in-planner"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Target size={15} className="shrink-0" />
                        <span className="truncate">{m.plannerCtx.special && !m.plannerCtx.species ? "Special draws in Planner" : "View in Planner"} · {[m.plannerCtx.species, m.plannerCtx.state].filter(Boolean).join(" ")}{m.plannerCtx.points != null ? ` · ${m.plannerCtx.points} pts` : ""}</span>
                      </span>
                      <ArrowUpRight size={16} className="shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </button>
                  )}
                </div>
              ))}
              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {suggestionsFor(aiContext).map((s) => (
                    <button key={s} onClick={() => send(s)} className="border border-[#232326] bg-[#111214] rounded-full px-3 py-2 text-xs text-[#B9B9B9] hover:border-[#FF5A1F] hover:text-[#F5F5F5] transition-colors" data-testid="ai-suggestion">{s}</button>
                  ))}
                </div>
              )}
            </div>

            {isAuthed && aiChips.length > 0 && (
              <div className="px-6 pt-2" data-testid="ai-personalization">
                <div className="bg-[#111214] border border-[#232326] rounded-lg">
                  <button type="button" onClick={() => setChipsOpen((v) => !v)} className="w-full flex items-center justify-between px-3.5 py-2.5" data-testid="ai-personalization-toggle-open">
                    <span className="flex items-center gap-2 text-xs text-[#B9B9B9]">
                      <Sparkles size={13} className="text-[#FF5A1F]" />
                      {personalOn ? <>Using your HomeRange <span className="text-[#7A7A7E]">· {aiChips.length - excluded.length} detail{aiChips.length - excluded.length === 1 ? "" : "s"}</span></> : <span className="text-[#7A7A7E]">Personalization off</span>}
                    </span>
                    <ChevronDown size={15} className={`text-[#7A7A7E] transition-transform ${chipsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {chipsOpen && (
                    <div className="px-3.5 pb-3.5 border-t border-[#232326] pt-3">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[11px] text-[#7A7A7E]">HomeRange AI will use these about you</span>
                        <button type="button" role="switch" aria-checked={personalOn} onClick={() => setPersonalOn((v) => !v)} data-testid="ai-personalization-master"
                          className={`relative w-9 h-5 rounded-full transition-colors ${personalOn ? "bg-[#FF5A1F]" : "bg-[#3A3A3E]"}`}>
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${personalOn ? "translate-x-4" : ""}`} />
                        </button>
                      </div>
                      <div className={`flex flex-wrap gap-2 ${personalOn ? "" : "opacity-40 pointer-events-none"}`}>
                        {aiChips.map((c) => {
                          const off = excluded.includes(c.key);
                          return (
                            <button key={c.key} type="button" onClick={() => setExcluded((e) => off ? e.filter((k) => k !== c.key) : [...e, c.key])}
                              data-testid={`ai-chip-${c.key}`}
                              className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${off ? "border-dashed border-[#3A3A3E] text-[#7A7A7E]" : "border-[#FF5A1F]/40 bg-[#FF5A1F]/10 text-[#F5F5F5]"}`}>
                              {c.label}{off ? <Plus size={11} /> : <X size={11} />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-6 pt-3 border-t border-[#232326] grid grid-cols-[1fr_auto] gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask HomeRange AI…" className="bg-[#111214] border border-[#232326] rounded-lg px-4 py-3 text-[#F5F5F5] placeholder:text-[#7A7A7E] outline-none focus:border-[#FF5A1F] transition-colors" data-testid="ai-input" />
              <button type="submit" disabled={busy} className="bg-[#FF5A1F] text-white px-4 rounded-lg hover:bg-[#FF6D33] transition-colors disabled:opacity-50 grid place-items-center" data-testid="ai-send"><Send size={18} /></button>
            </form>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

export function FloatingAI() {
  const { openAI } = useUI();
  return (
    <button onClick={() => openAI()} className="fixed z-[61] right-5 bottom-5 bg-[#FF5A1F] text-white rounded-full pl-4 pr-5 py-3.5 flex items-center gap-2 shadow-xl hover:bg-[#FF6D33] transition-colors" data-testid="floating-ai">
      <Sparkles size={18} /> <strong className="text-sm">HomeRange AI</strong>
    </button>
  );
}
